import { FC } from 'react'

import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import { getIsLanguageActive } from 'src/Pages/Artist/SpotifySchedulePage/helpers/helpers'
import { useBootstrapSpotifyCampaignBudget } from 'src/Pages/Artist/SpotifySchedulePage/helpers/useBootstrapSpotifyCampaignBudget'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { useSpotifyCampaignMatching } from 'src/Redux/spotifyCampaignMatchingApi'
import {
  ScheduleTrackStepButton,
  StepButtonStatus,
} from 'src/Containers/ui/ScheduleTrackStepButton'
import { MAX_TABLET_WIDTH } from 'src/Styled/variables'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { ModalType, ScheduleSteps } from 'src/Types/ui'

import { SpotifySettingsBudget } from './SpotifySettingsBudget'

export const getIsSettingsReady = (
  isGenresActive: boolean,
  isFetching: boolean,
  isLanguageActive: boolean,
  isAiUsageActive: boolean,
  budgetRangeError: boolean,
  isPreReleaseReady: boolean,
): boolean => {
  const hasIncompleteStep =
    isGenresActive || isLanguageActive || isAiUsageActive
  return (
    !hasIncompleteStep && !isFetching && !budgetRangeError && isPreReleaseReady
  )
}

const getLanguagesStatus = (
  isGenresActive: boolean,
  isLanguageActive: boolean,
): StepButtonStatus => {
  if (isGenresActive && isLanguageActive) {
    return StepButtonStatus.pending
  }
  if (isLanguageActive) {
    return StepButtonStatus.active
  }
  return StepButtonStatus.completed
}

const getAiUsageStatus = (
  isGenresActive: boolean,
  isLanguageActive: boolean,
  isAiUsageActive: boolean,
): StepButtonStatus => {
  if ((isGenresActive || isLanguageActive) && isAiUsageActive) {
    return StepButtonStatus.pending
  }
  if (isAiUsageActive) {
    return StepButtonStatus.active
  }
  return StepButtonStatus.completed
}

const getTrackFileStatus = (
  hasReleaseDate: boolean,
  hasTrackFile: boolean,
): StepButtonStatus => {
  if (hasTrackFile) {
    return StepButtonStatus.completed
  }
  return hasReleaseDate ? StepButtonStatus.active : StepButtonStatus.pending
}

const getGenresStatus = (
  isPreReleaseReady: boolean,
  isGenresActive: boolean,
): StepButtonStatus => {
  if (!isPreReleaseReady) {
    return StepButtonStatus.pending
  }
  return isGenresActive ? StepButtonStatus.active : StepButtonStatus.completed
}

const Container = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  @media screen and (min-width: 767px) {
    width: 324px;
  }
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`
const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`
const StepsButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

const StyledSkeleton = styled(PrimarySkeleton)`
  width: 100%;
  height: 500px;
  border-radius: 16px;
  @media screen and (min-width: 767px) {
    width: 324px;
  }
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
`

interface PreReleaseStepsProps {
  handleClick: (type: ModalType) => void
  hasReleaseDate: boolean
  hasTrackFile: boolean
}

const renderPreReleaseSteps = ({
  handleClick,
  hasReleaseDate,
  hasTrackFile,
}: PreReleaseStepsProps): React.ReactNode => (
  <>
    <ScheduleTrackStepButton
      onClick={() => handleClick(ModalType.ReleaseDate)}
      status={
        hasReleaseDate ? StepButtonStatus.completed : StepButtonStatus.active
      }
    >
      add release date
    </ScheduleTrackStepButton>
    <ScheduleTrackStepButton
      onClick={() => handleClick(ModalType.TrackFile)}
      status={getTrackFileStatus(hasReleaseDate, hasTrackFile)}
    >
      add track file
    </ScheduleTrackStepButton>
  </>
)

interface CampaignStepListProps extends PreReleaseStepsProps {
  isAiUsageActive: boolean
  isGenresActive: boolean
  isLanguageActive: boolean
  isPreRelease: boolean
}

const renderCampaignStepList = ({
  handleClick,
  hasReleaseDate,
  hasTrackFile,
  isAiUsageActive,
  isGenresActive,
  isLanguageActive,
  isPreRelease,
}: CampaignStepListProps): React.ReactNode => {
  const isPreReleaseReady = !isPreRelease || (hasReleaseDate && hasTrackFile)

  return (
    <Container>
      <Title>To set up campaign budget complete these steps:</Title>
      <StepsButtonContainer>
        {isPreRelease &&
          renderPreReleaseSteps({
            handleClick,
            hasReleaseDate,
            hasTrackFile,
          })}
        <ScheduleTrackStepButton
          onClick={() => handleClick(ModalType.Genres)}
          status={getGenresStatus(isPreReleaseReady, isGenresActive)}
        >
          add target genres
        </ScheduleTrackStepButton>
        <ScheduleTrackStepButton
          onClick={() => handleClick(ModalType.Languages)}
          status={getLanguagesStatus(isGenresActive, isLanguageActive)}
        >
          add track language
        </ScheduleTrackStepButton>
        <ScheduleTrackStepButton
          onClick={() => handleClick(ModalType.AiUsage)}
          status={getAiUsageStatus(
            isGenresActive,
            isLanguageActive,
            isAiUsageActive,
          )}
        >
          add AI usage info
        </ScheduleTrackStepButton>
      </StepsButtonContainer>
    </Container>
  )
}

export const SpotifyCampaignSteps: FC = () => {
  const { setSearch } = useCustomSearchParams()
  const params = useParams()
  const { campaignDetails, isPreRelease, releaseDate, trackFile } =
    useSpotifyCampaignStatus()

  useBootstrapSpotifyCampaignBudget()

  const genres = campaignDetails?.genres ?? []
  const languages = campaignDetails?.languages ?? []
  const isGenresActive = !genres.length
  const isLanguageActive = getIsLanguageActive(languages)
  const isAiUsageActive = !campaignDetails?.aiUsage
  const { isError: budgetRangeError, isFetching } = useBudgetRange()
  const { maxCuratorsCount } = useSpotifyCampaignMatching()

  const handleClick = (type: ModalType): void => {
    setSearch({ step: ScheduleSteps.SETTINGS, modal: type })
  }

  const isCampaignValid = Number(campaignDetails?.id) === Number(params.number)
  const isSettingsReady = getIsSettingsReady(
    isGenresActive,
    isFetching,
    isLanguageActive,
    isAiUsageActive,
    budgetRangeError,
    !isPreRelease || (Boolean(releaseDate) && Boolean(trackFile)),
  )
  const showSettings = isSettingsReady && isCampaignValid
  const isMatchingReady = maxCuratorsCount !== null

  if (isFetching || (showSettings && !isMatchingReady)) {
    return <StyledSkeleton />
  }
  if (showSettings) {
    return <SpotifySettingsBudget />
  }

  const hasReleaseDate = Boolean(releaseDate)
  const hasTrackFile = Boolean(trackFile)
  return renderCampaignStepList({
    handleClick,
    hasReleaseDate,
    hasTrackFile,
    isAiUsageActive,
    isGenresActive,
    isLanguageActive,
    isPreRelease,
  })
}
